import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Heading,
    Text,
    Button,
    FormControl,
    FormLabel,
    Spinner,
    Alert,
    AlertIcon,
    VStack,
    HStack,
    useToast,
    Grid,
    GridItem,
    IconButton
} from '@chakra-ui/react';
import { FaFileExcel } from 'react-icons/fa';

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

const RequestSummariesDashboard = () => {
    const [selectedLevels, setSelectedLevels] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [requestSummariesData, setRequestSummariesData] = useState(null);
    const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
    const [loadingGrades, setLoadingGrades] = useState(true);
    const toast = useToast();

    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");

    // Generate months starting from October 2024
    const generateMonthOptions = () => {
        const months = [];
        const startDate = new Date(2024, 9, 1); // October 2024 (month 9 = October, 0-indexed)
        const currentDate = new Date();
        
        // Calculate how many months from October 2024 to current month
        const monthsDiff = (currentDate.getFullYear() - startDate.getFullYear()) * 12 + 
                          (currentDate.getMonth() - startDate.getMonth()) + 1;
        
        for (let i = 0; i < monthsDiff; i++) {
            const date = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
            const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            const monthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            months.push({
                value: monthValue,
                label: monthName,
                date: date
            });
        }
        
        return months;
    };

    const monthOptions = generateMonthOptions();

    // Fetch grade levels from API
    const fetchGradeLevels = useCallback(async () => {
        try {
            setLoadingGrades(true);
            const response = await fetch(`${API_BASE_URL}?method=getDDjobgrade&huserID=${HuserID}&uuid=${UUID}&HclientID=11`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data && Array.isArray(data)) {
                // Transform the data to match the expected format
                const transformedData = data.map(item => ({
                    value: item.ddjobgradeID.toString(),
                    label: `Grade ${item.gradeLevel}`
                }));
                setGradeLevelOptions(transformedData);
            } else {
                throw new Error('Invalid data format received from API');
            }
            
        } catch (error) {
            console.error('Error fetching grade levels:', error);
            toast({
                title: "Error loading grade levels",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setLoadingGrades(false);
        }
    }, [HuserID, UUID, toast]);

    // Set default selected months (current month + last 2 months = 3 months total)
    useEffect(() => {
        const defaultMonths = monthOptions.slice(-3).map(month => month.value);
        setSelectedMonths(defaultMonths);
        
        // Fetch grade levels on component mount
        fetchGradeLevels();
    }, [fetchGradeLevels]);

    // Set all grade levels as selected by default when they are loaded
    useEffect(() => {
        if (gradeLevelOptions.length > 0 && selectedLevels.length === 0) {
            const allGradeValues = gradeLevelOptions.map(option => option.value);
            setSelectedLevels(allGradeValues);
        }
    }, [gradeLevelOptions, selectedLevels.length]);

    const buildApiUrl = useCallback((levels, months) => {
        const gradeList = levels.join(',');
        let url = `${API_BASE_URL}?method=DashboardRequestSummary&huserID=${HuserID}&uuid=${UUID}&gradeList=${gradeList}`;
        
        if (months.length > 0) {
            const startMonth = months[0];
            const endMonth = months[months.length - 1];
            url += `&startMonth=${startMonth}&endMonth=${endMonth}`;
        }
        
        return url;
    }, [HuserID, UUID]);

    const fetchDashboardData = useCallback(async () => {
        if (selectedLevels.length === 0) {
            setError('Please select at least one grade level.');
            return;
        }

        if (selectedMonths.length === 0) {
            setError('Please select at least one reporting period.');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            const endpoint = buildApiUrl(selectedLevels, selectedMonths);
            const cacheBustingUrl = `${endpoint}&_t=${Date.now()}`;
            
            const response = await fetch(cacheBustingUrl);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // Get the response as text first
            const responseText = await response.text();
            console.log('Raw response:', responseText);
            
            // Clean the response - remove any HTML or extra content
            let cleanedResponse = responseText.trim();
            
            // Find the JSON part by looking for the first '[' and last ']'
            const firstBracket = cleanedResponse.indexOf('[');
            const lastBracket = cleanedResponse.lastIndexOf(']');
            
            if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
                cleanedResponse = cleanedResponse.substring(firstBracket, lastBracket + 1);
            }
            
            console.log('Cleaned response:', cleanedResponse);
            
            // Try to parse the JSON
            let data;
            try {
                data = JSON.parse(cleanedResponse);
            } catch (parseError) {
                console.error('JSON parse error:', parseError);
                console.error('Cleaned response:', cleanedResponse);
                throw new Error(`Invalid JSON response: ${parseError.message}`);
            }
            
            if (data && data.length > 0) {
                setRequestSummariesData(data[0]); // Store the first item from the response
                
                toast({
                    title: "Request Summaries Report Generated",
                    description: "Report data loaded successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                setError('No request summaries data found for the selected criteria.');
            }
            
        } catch (error) {
            console.error('Error generating request summaries report:', error);
            setError(error.message);
            toast({
                title: "Error generating request summaries report",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [selectedLevels, selectedMonths, buildApiUrl, toast]);

    const handleLevelChange = (event) => {
        const value = event.target.value;
        setSelectedLevels(prev => {
            if (prev.includes(value)) {
                return prev.filter(level => level !== value);
            } else {
                return [...prev, value];
            }
        });
    };

    const handleMonthClick = (monthValue) => {
        const monthIndex = monthOptions.findIndex(m => m.value === monthValue);
        
        if (selectedMonths.includes(monthValue)) {
            // If clicking on a selected month, check if we can deselect it while maintaining sequence
            const selectedIndices = selectedMonths.map(m => monthOptions.findIndex(opt => opt.value === m)).sort((a, b) => a - b);
            
            // Only allow deselection if it's at the start or end of the selection
            if (monthIndex === selectedIndices[0] || monthIndex === selectedIndices[selectedIndices.length - 1]) {
                setSelectedMonths(prev => prev.filter(m => m !== monthValue));
            }
        } else {
            // If clicking on an unselected month, extend the selection to include it
            if (selectedMonths.length === 0) {
                setSelectedMonths([monthValue]);
            } else {
                const selectedIndices = selectedMonths.map(m => monthOptions.findIndex(opt => opt.value === m)).sort((a, b) => a - b);
                const minSelected = selectedIndices[0];
                const maxSelected = selectedIndices[selectedIndices.length - 1];
                
                let newStartIndex, newEndIndex;
                
                if (monthIndex < minSelected) {
                    // Extend backwards
                    newStartIndex = monthIndex;
                    newEndIndex = maxSelected;
                } else if (monthIndex > maxSelected) {
                    // Extend forwards
                    newStartIndex = minSelected;
                    newEndIndex = monthIndex;
                } else {
                    // Fill gaps (shouldn't happen with sequential requirement, but just in case)
                    newStartIndex = Math.min(minSelected, monthIndex);
                    newEndIndex = Math.max(maxSelected, monthIndex);
                }
                
                const newSelectedMonths = [];
                for (let i = newStartIndex; i <= newEndIndex; i++) {
                    newSelectedMonths.push(monthOptions[i].value);
                }
                setSelectedMonths(newSelectedMonths);
            }
        }
    };

    // Handle Excel generation
    const handleGenerateExcel = () => {
        if (!requestSummariesData) {
            toast({
                title: "No data available",
                description: "Please generate a report first before creating Excel file.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            // Build the Excel URL with the same parameters
            const gradeList = selectedLevels.join(',');
            const startMonth = selectedMonths[0];
            const endMonth = selectedMonths[selectedMonths.length - 1];
            
            const excelUrl = `https://qaapirgeneng.digitalfields.co.za/ws1.cfc?method=DashboardRequestSummaryExcel&huserID=${HuserID}&uuid=${UUID}&gradeList=${gradeList}&startMonth=${startMonth}&endMonth=${endMonth}`;
            
            // Use fetch to get the Excel file as blob and then download it
            fetch(excelUrl)
                .then(response => response.blob())
                .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'request-summaries-report.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    toast({
                        title: "Excel Report Generated",
                        description: "Excel report is being downloaded.",
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                    });
                })
                .catch(error => {
                    console.error('Error downloading Excel:', error);
                    toast({
                        title: "Error downloading Excel",
                        description: error.message,
                        status: "error",
                        duration: 6000,
                        isClosable: true,
                    });
                });
            
        } catch (error) {
            console.error('Error generating Excel:', error);
            toast({
                title: "Error generating Excel",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        }
    };

    return (
        <Box p={8} maxW="100%" overflowX="auto">
            <Heading mb={6} fontSize="2xl">Request Summaries Dashboard</Heading>
            
            {/* Grade Level Selection and Reporting Period Selection */}
            <Grid templateColumns="1fr 1fr" gap={6} mb={8}>
                {/* Grade Level Selection */}
                <GridItem>
                    <Box p={6} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" h="100%">
                        <FormControl mb={6}>
                            <FormLabel fontWeight="bold" fontSize="lg">Select Grade Levels:</FormLabel>
                            {loadingGrades ? (
                                <Box textAlign="center" py={4}>
                                    <Spinner size="sm" />
                                    <Text mt={2} fontSize="sm">Loading grade levels...</Text>
                                </Box>
                            ) : (
                                <VStack align="start" spacing={3}>
                                    {gradeLevelOptions.map(option => (
                                        <HStack key={option.value} spacing={3}>
                                            <input
                                                type="checkbox"
                                                value={option.value}
                                                checked={selectedLevels.includes(option.value)}
                                                onChange={handleLevelChange}
                                                style={{ transform: 'scale(1.2)' }}
                                            />
                                            <Text fontSize="md">{option.label}</Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            )}
                        </FormControl>
                    </Box>
                </GridItem>

                {/* Reporting Period Selection */}
                <GridItem>
                    <Box p={6} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" h="100%">
                        <FormControl mb={6}>
                            <FormLabel fontWeight="bold" fontSize="lg">Select Reporting Period:</FormLabel>
                            <Text fontSize="sm" color="gray.600" mb={4}>
                                Click months to select a sequential range. Default: Last 3 months including current.
                            </Text>
                            <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                                {monthOptions.map(month => (
                                    <Button
                                        key={month.value}
                                        size="sm"
                                        variant={selectedMonths.includes(month.value) ? "solid" : "outline"}
                                        colorScheme={selectedMonths.includes(month.value) ? "orange" : "gray"}
                                        onClick={() => handleMonthClick(month.value)}
                                        fontSize="xs"
                                        p={2}
                                        height="auto"
                                        whiteSpace="normal"
                                        textAlign="center"
                                    >
                                        {month.label}
                                    </Button>
                                ))}
                            </Grid>
                        </FormControl>
                    </Box>
                </GridItem>
            </Grid>

            {/* Generate Report Button */}
            <Box textAlign="center" mb={8}>
                <Button 
                    colorScheme="blue" 
                    size="lg"
                    onClick={fetchDashboardData}
                    isLoading={loading}
                    loadingText="Generating..."
                    isDisabled={selectedLevels.length === 0 || selectedMonths.length === 0}
                >
                    Generate Report
                </Button>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box textAlign="center" py={12}>
                    <Spinner size="xl" />
                    <Text mt={4} fontSize="lg">Loading request summaries data...</Text>
                </Box>
            )}

            {/* Error State */}
            {error && !loading && (
                <Alert status="error" mb={6} fontSize="md">
                    <AlertIcon />
                    {error}
                </Alert>
            )}

            {/* Content */}
            {!loading && !error && requestSummariesData && (
                <Box 
                    border="1px solid" 
                    borderColor="gray.300" 
                    borderRadius="lg" 
                    p={6} 
                    bg="white"
                    boxShadow="sm"
                >
                    {/* Header with Excel Icon */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
                        <Heading size="lg">Request Summaries Report</Heading>
                        <IconButton
                            aria-label="Generate Excel Report"
                            icon={<FaFileExcel />}
                            colorScheme="green"
                            size="lg"
                            onClick={handleGenerateExcel}
                            title="Generate Excel Report"
                            _hover={{
                                bg: "green.600",
                                transform: "scale(1.05)"
                            }}
                            transition="all 0.2s"
                        />
                    </Box>
                    
                    {/* First Table - Grade Levels */}
                    {requestSummariesData.gradeMonthColArr && requestSummariesData.gradeMonthArr && (
                        <Box mb={8}>
                            <Heading size="md" mb={4}>By Grade Level</Heading>
                            <Box overflowX="auto">
                                <table style={{ 
                                    width: '100%', 
                                    borderCollapse: 'collapse',
                                    fontSize: '14px',
                                    fontFamily: 'Arial, sans-serif'
                                }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f7fafc' }}>
                                            {requestSummariesData.gradeMonthColArr[0].map((header, index) => (
                                                <th key={index} style={{ 
                                                    border: '1px solid #e2e8f0',
                                                    padding: '12px 8px',
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    backgroundColor: index === 0 ? '#edf2f7' : '#f7fafc',
                                                    minWidth: '120px'
                                                }}>
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requestSummariesData.gradeMonthArr.map((row, rowIndex) => {
                                            const isLastRow = rowIndex === requestSummariesData.gradeMonthArr.length - 1;
                                            return (
                                                <tr key={rowIndex} style={{ 
                                                    backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f9f9f9',
                                                    fontWeight: isLastRow ? '900' : 'normal',
                                                    borderTop: isLastRow ? '2px solid #2d3748' : 'none'
                                                }}>
                                                    {row.map((cell, cellIndex) => (
                                                        <td key={cellIndex} style={{ 
                                                            border: '1px solid #e2e8f0',
                                                            padding: '10px 8px',
                                                            textAlign: cellIndex === 0 ? 'left' : 'center',
                                                            fontWeight: isLastRow ? '900' : (cellIndex === 0 ? 'bold' : 'normal'),
                                                            backgroundColor: isLastRow ? '#f0f0f0' : 'transparent'
                                                        }}>
                                                            {cell}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>
                        </Box>
                    )}

                    {/* Second Table - Operating Divisions */}
                    {requestSummariesData.gradeMonthDeptColArr && requestSummariesData.gradeMonthDeptArr && (
                        <Box>
                            <Heading size="md" mb={4}>By Operating Division</Heading>
                            <Box overflowX="auto">
                                <table style={{ 
                                    width: '100%', 
                                    borderCollapse: 'collapse',
                                    fontSize: '14px',
                                    fontFamily: 'Arial, sans-serif'
                                }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f7fafc' }}>
                                            {requestSummariesData.gradeMonthDeptColArr[0].map((header, index) => (
                                                <th key={index} style={{ 
                                                    border: '1px solid #e2e8f0',
                                                    padding: '12px 8px',
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    backgroundColor: index === 0 ? '#edf2f7' : '#f7fafc',
                                                    minWidth: '120px'
                                                }}>
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requestSummariesData.gradeMonthDeptArr.map((row, rowIndex) => {
                                            const isLastRow = rowIndex === requestSummariesData.gradeMonthDeptArr.length - 1;
                                            return (
                                                <tr key={rowIndex} style={{ 
                                                    backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f9f9f9',
                                                    fontWeight: isLastRow ? '900' : 'normal',
                                                    borderTop: isLastRow ? '2px solid #2d3748' : 'none'
                                                }}>
                                                    {row.map((cell, cellIndex) => (
                                                        <td key={cellIndex} style={{ 
                                                            border: '1px solid #e2e8f0',
                                                            padding: '10px 8px',
                                                            textAlign: cellIndex === 0 ? 'left' : 'center',
                                                            fontWeight: isLastRow ? '900' : (cellIndex === 0 ? 'bold' : 'normal'),
                                                            backgroundColor: isLastRow ? '#f0f0f0' : 'transparent'
                                                        }}>
                                                            {cell}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>
                        </Box>
                    )}
                    
                    {/* Fallback if data structure is different */}
                    {(!requestSummariesData.gradeMonthColArr || !requestSummariesData.gradeMonthArr) && 
                     (!requestSummariesData.gradeMonthDeptColArr || !requestSummariesData.gradeMonthDeptArr) && (
                        <Box p={4} bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="md">
                            <Text fontWeight="bold" color="orange.600">Data Structure Issue</Text>
                            <Text fontSize="sm" mt={2}>
                                Expected data structure not found. Raw data:
                            </Text>
                            <pre style={{ 
                                fontSize: '10px', 
                                backgroundColor: '#f5f5f5', 
                                padding: '10px', 
                                borderRadius: '4px',
                                overflow: 'auto',
                                maxHeight: '200px'
                            }}>
                                {JSON.stringify(requestSummariesData, null, 2)}
                            </pre>
                        </Box>
                    )}
                </Box>
            )}

            {/* Initial State Message */}
            {!loading && !error && !requestSummariesData && (
                <Alert status="info" fontSize="md">
                    <AlertIcon />
                    Please select grade levels and reporting period, then click "Generate Report" to view the request summaries dashboard.
                </Alert>
            )}
        </Box>
    );
};

export default RequestSummariesDashboard;
